const root = document.documentElement;
root.classList.add('nmvx-ui-enabled');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let readingProgressCleanup = null;
let activeProgressElement = null;

function initReveal() {
  if (reduceMotion || !('IntersectionObserver' in window)) return;

  const candidates = Array.from(
    document.querySelectorAll('#MainContent > .shopify-section, #MainContent > .section-wrapper, #MainContent > .nmvx-page-boost')
  ).filter((element) => element.dataset.nmvxRevealInit !== 'true');

  if (!candidates.length) return;

  const observer = new IntersectionObserver((entries, currentObserver) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('nmvx-reveal-in');
      currentObserver.unobserve(entry.target);
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

  for (const element of candidates) {
    element.dataset.nmvxRevealInit = 'true';
    const rect = element.getBoundingClientRect();
    if (rect.top <= window.innerHeight * 0.82) continue;
    element.classList.add('nmvx-reveal-pending');
    observer.observe(element);
  }
}

function initReadingProgress() {
  const progress = document.querySelector('[data-nmvx-reading-progress]');
  if (!progress) {
    readingProgressCleanup?.();
    readingProgressCleanup = null;
    activeProgressElement = null;
    return;
  }

  if (progress === activeProgressElement) return;

  readingProgressCleanup?.();
  readingProgressCleanup = null;
  activeProgressElement = progress;

  const bar = progress.querySelector('.nmvx-reading-progress__bar');
  const article = document.querySelector('[data-nmvx-article-content]')
    || document.querySelector('.blog-post-content')
    || document.querySelector('[data-template^="article"] .rte')
    || document.querySelector('article');
  const pageWrapper = document.querySelector('.page-wrapper');

  if (!bar || !article) {
    progress.hidden = true;
    return;
  }

  const getScroller = () => {
    if (!pageWrapper) return window;
    const style = window.getComputedStyle(pageWrapper);
    const isScrollable = /(auto|scroll)/.test(style.overflowY) && pageWrapper.scrollHeight > pageWrapper.clientHeight;
    return isScrollable ? pageWrapper : window;
  };

  let ticking = false;
  const update = () => {
    ticking = false;
    const scroller = getScroller();
    const articleRect = article.getBoundingClientRect();
    let scrollTop = 0;
    let viewportHeight = window.innerHeight;
    let articleTop = 0;

    if (scroller === window) {
      scrollTop = window.scrollY;
      viewportHeight = window.innerHeight;
      articleTop = scrollTop + articleRect.top;
    } else {
      const scrollerRect = scroller.getBoundingClientRect();
      scrollTop = scroller.scrollTop;
      viewportHeight = scroller.clientHeight;
      articleTop = scrollTop + articleRect.top - scrollerRect.top;
    }

    const start = articleTop - viewportHeight * 0.15;
    const end = articleTop + article.offsetHeight - viewportHeight * 0.65;
    const available = Math.max(1, end - start);
    const percent = Math.min(100, Math.max(0, ((scrollTop - start) / available) * 100));
    bar.style.width = `${percent.toFixed(2)}%`;
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  pageWrapper?.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });

  readingProgressCleanup = () => {
    window.removeEventListener('scroll', requestUpdate);
    pageWrapper?.removeEventListener('scroll', requestUpdate);
    window.removeEventListener('resize', requestUpdate);
  };
}

function initDetailsGroups() {
  for (const group of document.querySelectorAll('[data-nmvx-details-group]')) {
    if (group.dataset.nmvxDetailsInit === 'true') continue;
    group.dataset.nmvxDetailsInit = 'true';

    group.addEventListener('toggle', (event) => {
      const opened = event.target;
      if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      for (const item of group.querySelectorAll('details[open]')) {
        if (item !== opened) item.removeAttribute('open');
      }
    }, true);
  }
}

let personalizationVariantSyncReady = false;

function initPersonalizationVariantSync() {
  if (personalizationVariantSyncReady) return;
  personalizationVariantSyncReady = true;

  document.addEventListener('shopify:product:select', (event) => {
    const completion = event?.promise;
    if (!completion || typeof completion.then !== 'function') return;

    completion
      .then(() => {
        const section = event.target instanceof Element
          ? event.target.closest('.shopify-section, product-information')
          : null;
        const card = section?.querySelector('[data-nmvx-personalization]');
        card?.dispatchEvent(new Event('change', { bubbles: true }));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[NAMVEXO personalization] Variant synchronization failed safely.', error);
        }
      });
  });
}

let personalizationFormDataReady = false;

function initPersonalizationFormDataCleanup() {
  if (personalizationFormDataReady) return;
  personalizationFormDataReady = true;

  document.addEventListener('formdata', (event) => {
    try {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !(event.formData instanceof FormData)) return;

      const formId = form.getAttribute('id');
      if (!formId) return;

      const card = Array.from(document.querySelectorAll('[data-nmvx-personalization]'))
        .find((item) => item.dataset.formId === formId);
      if (!card || card.dataset.mode !== 'ammo') return;

      const textInput = card.querySelector('[data-nmvx-primary]');
      const fileInput = card.querySelector('[data-nmvx-file]');

      if (textInput instanceof HTMLInputElement && !textInput.value.trim()) {
        event.formData.delete(textInput.name);
      }

      if (fileInput instanceof HTMLInputElement) {
        const selectedFile = fileInput.files?.[0];
        if (!selectedFile?.name) event.formData.delete(fileInput.name);
      }
    } catch (error) {
      console.error('[NAMVEXO personalization] FormData cleanup failed safely.', error);
    }
  });
}

function boot() {
  initReveal();
  initReadingProgress();
  initDetailsGroups();
  initPersonalizationVariantSync();
  initPersonalizationFormDataCleanup();
  root.classList.add('nmvx-ui-ready');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener('shopify:section:load', boot);
